import { FormEventHandler, useEffect, useState } from "react";
import { Form } from "@remix-run/react";
import { RoundedCta } from "~/components/ctas";
import { CaptureConfig, Mapping } from "../newsletter/formCapture.types";
import { MultiselectOption, MultiselectOptions } from "./MultiselectOptions";
import FieldRow50 from "./FieldRow50";
import SubmissionComplete from "./SubmissionComplete";

type HtmlStringFormCaptureProps = {
  html: string;
};

const inputStyle =
  "body border-b-1 col-span-4 w-full border border-black rounded-sm px-4 py-2 mt-2 placeholder:placeholder-gray-400 focus:outline-1 lg:col-start-2";

const multiselectOptions: MultiselectOption[] = [
  {
    value: "climate_and_energy",
    label: "Climate and Energy",
    dataverseValue: "860210000",
  },
  {
    value: "digital_transformation",
    label: "Digital Transformation of Government",
    dataverseValue: "860210003",
  },
  {
    value: "geopolitics_and_trade",
    label: "Geopolitics, Economic Prosperity and Trade",
    dataverseValue: "860210002",
  },
  {
    value: "healthcare",
    label: "Healthcare, particularly pivot to prevention",
    dataverseValue: "860210001",
  },
];

const communityPreferences = {
  FormFieldName: "embedform_community_preferences",
  DataverseFieldName: "tbi_whichofourcommunitieswouldbeofrelevance",
  DataverseFieldValue: multiselectOptions.map((option) => ({
    FormValue: option.value,
    DataverseValue: option.dataverseValue,
  })),
};

// Define the mappings
const mappings: Mapping[] = [
  {
    FormFieldName: "embedform_first_name",
    DataverseFieldName: "firstname",
  },
  {
    FormFieldName: "embedform_last_name",
    DataverseFieldName: "lastname",
  },
  {
    FormFieldName: "embedform_organisation",
    DataverseFieldName: "adx_organizationname",
  },
  {
    FormFieldName: "embedform_job_title",
    DataverseFieldName: "jobtitle",
  },
  {
    FormFieldName: "embedform_email",
    DataverseFieldName: "emailaddress1",
  },
  communityPreferences,
  {
    FormFieldName: "embedform_innovation_perspective",
    DataverseFieldName: "tbi_wheredoyouofferauniqueperspectiveonpolicy",
  },
  {
    FormFieldName: "embedform_innovation_solution",
    DataverseFieldName: "tbi_ifrelevantwhattechnologysolutionorproduct",
  },
];

const captureConfig: CaptureConfig = {
  FormId: "01bf525a-1621-f011-998a-7c1e527906e6",
  FormApiUrl:
    "https://public-gbr.mkt.dynamics.com/api/v1.0/orgs/7a2dad3e-a1b3-4828-98dd-e6bd8c664cde/landingpageforms",
};

export default function PartnershipsPlatformLead({
  html,
}: HtmlStringFormCaptureProps) {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!window.d365mktformcapture) {
      return;
    }

    const form = e.currentTarget as HTMLFormElement;
    const serializedForm = window.d365mktformcapture.serializeForm(
      form,
      mappings
    );
    const payload = serializedForm.SerializedForm.build();
    try {
      await window.d365mktformcapture.submitForm(captureConfig, payload);
    } catch (e) {
      console.log(e, "error");
    }
  };

  useEffect(() => {
    if (window.d365mktformcapture) {
      return; // Already loaded
    }
    const script = document.createElement("script");
    script.src =
      "https://cxppusa1formui01cdnsa01-endpoint.azureedge.net/gbr/FormCapture/FormCapture.bundle.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  if (submitted) {
    return <SubmissionComplete />;
  }

  return (
    <Form
      className="col-span-full m-auto mt-8 text-black lg:col-span-8 lg:col-start-3"
      onSubmit={handleSubmit}
      id="partnerships-platform-lead"
    >
      <FieldRow50
        options={[
          {
            label: "First Name",
            value: "embedform_first_name",
            isRequired: true,
          },
          {
            label: "Last Name",
            value: "embedform_last_name",
            isRequired: true,
          },
        ]}
        inputStyle={inputStyle}
      />
      <FieldRow50
        options={[
          {
            label: "Organisation",
            value: "embedform_organisation",
            isRequired: true,
          },
          {
            label: "Job Title",
            value: "embedform_job_title",
            isRequired: true,
          },
        ]}
        inputStyle={inputStyle}
        rowStyle="pt-6"
      />

      <div className="pt-6">
        <label htmlFor="embedform_email" className="block font-bold">
          Email *
        </label>
        <input
          type={"email"}
          id="embedform_email"
          name="embedform_email"
          className={inputStyle}
          placeholder="Email"
          required
        />
      </div>
      <div className="pt-6">
        <div className="block font-bold">
          Select which of our communities would be of relevance and interest to
          you:*
        </div>
        <MultiselectOptions
          options={multiselectOptions}
          formFieldName={communityPreferences.FormFieldName}
          isRequired
        />
      </div>
      <div className="pt-6">
        <label
          htmlFor="embedform_innovation_perspective"
          className="block font-bold"
        >
          Where do you offer a unique perspective on policy debate and
          innovation?*
        </label>
        <textarea
          name="embedform_innovation_perspective"
          rows={4}
          required
          className={inputStyle}
        />
      </div>
      <div className="pt-6">
        <label
          htmlFor="embedform_innovation_solution"
          className="block font-bold"
        >
          If relevant, what technology solution or product do you have that
          could be applicable as is, or transformed, to create real lasting
          change for all?*
        </label>
        <textarea
          name="embedform_innovation_solution"
          rows={4}
          required
          className={inputStyle}
        />
      </div>
      <RoundedCta
        kind="black"
        label="Submit"
        type="submit"
        className="mt-6 px-6 py-2"
      />
    </Form>
  );
}
